import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { Plus, CheckCircle2, Trash2, Link2, AlertTriangle, Edit3, X } from 'lucide-react';

const EMPTY = {
  state_slug: '', category: '', display_title: '', description: '', language: 'English',
  adult_or_minor: 'Adult', witness_required: null, notarization_required: null,
  clinician_signature_required: null, electronic_registry: false, official_source: '',
  official_url: '', pdf_url: '', instructions: '', revocation_instructions: '',
  agent_eligibility: '', age_requirement: '18+', registry_info: '', pregnancy_provisions: '',
  status: 'Current', published: true,
};

const TriSelect = ({ label, value, onChange }) => (
  <label className="text-sm">{label}
    <select className="w-full border rounded-md h-9 px-2 mt-1" value={value === null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}>
      <option value="">Unknown</option><option value="true">Yes</option><option value="false">No</option>
    </select>
  </label>
);

const LegalFormsAdmin = () => {
  const { user } = useAuth();
  const [data, setData] = useState({ forms: [], categories: [], states: [], statuses: [] });
  const [editing, setEditing] = useState(null);
  const [reviewDue, setReviewDue] = useState(0);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/legal-forms`);
      setData(r.data);
      const rd = await axios.get(`${API_URL}/admin/legal-forms/alerts/review-due`);
      setReviewDue(rd.data.count);
    } catch (e) { toast.error('Could not load (admin only).'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.state_slug || !editing.category || !editing.display_title) { toast.error('State, document type and title are required.'); return; }
    try {
      if (editing.form_id) await axios.put(`${API_URL}/admin/legal-forms/${editing.form_id}`, editing);
      else await axios.post(`${API_URL}/admin/legal-forms`, editing);
      toast.success('Saved.'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
  };
  const verify = async (id) => { await axios.post(`${API_URL}/admin/legal-forms/${id}/verify`); toast.success('Verified.'); load(); };
  const del = async (id) => { if (!window.confirm('Delete this form?')) return; await axios.delete(`${API_URL}/admin/legal-forms/${id}`); toast.success('Deleted.'); load(); };
  const checkLinks = async () => {
    setChecking(true);
    try { const r = await axios.post(`${API_URL}/admin/legal-forms/check-links`); toast.success(`Checked ${r.data.checked} links — ${r.data.broken_count} broken.`); load(); }
    catch (e) { toast.error('Link check failed.'); } finally { setChecking(false); }
  };

  if (user && user.user_type !== 'admin') return <div className="min-h-screen pt-28 text-center text-gray-600">Admin access required.</div>;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary">Advance Directives & Legal Forms — Admin</h1>
            <p className="text-gray-500 text-sm">{data.forms.length} forms · {reviewDue > 0 && <span className="text-amber-700 font-semibold">{reviewDue} need review</span>}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={checkLinks} disabled={checking} data-testid="check-links-btn"><Link2 className="w-4 h-4 mr-1" /> {checking ? 'Checking…' : 'Check links'}</Button>
            <Button onClick={() => setEditing({ ...EMPTY })} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="add-form-btn"><Plus className="w-4 h-4 mr-1" /> Add form</Button>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500"><tr>
              <th className="p-3">State</th><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Verified</th><th className="p-3">Published</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {data.forms.map((f) => (
                <tr key={f.form_id} className="border-t" data-testid={`admin-row-${f.form_id}`}>
                  <td className="p-3">{f.abbr}</td>
                  <td className="p-3 font-medium text-primary">{f.display_title}</td>
                  <td className="p-3 text-gray-500">{f.category_label}</td>
                  <td className="p-3">{f.status}</td>
                  <td className="p-3 text-gray-500">{f.last_verified ? new Date(f.last_verified).toLocaleDateString() : '—'}</td>
                  <td className="p-3">{f.published ? 'Yes' : 'Draft'}</td>
                  <td className="p-3 flex gap-1">
                    <button onClick={() => verify(f.form_id)} title="Verify" className="p-1.5 hover:bg-green-50 rounded"><CheckCircle2 className="w-4 h-4 text-green-600" /></button>
                    <button onClick={() => setEditing(f)} title="Edit" className="p-1.5 hover:bg-gray-100 rounded"><Edit3 className="w-4 h-4 text-gray-600" /></button>
                    <button onClick={() => del(f.form_id)} title="Delete" className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-xl font-bold text-primary">{editing.form_id ? 'Edit' : 'Add'} form</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">State
                <select className="w-full border rounded-md h-9 px-2 mt-1" value={editing.state_slug} onChange={(e) => setEditing({ ...editing, state_slug: e.target.value })} data-testid="edit-state">
                  <option value="">Select…</option>{data.states.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
              </label>
              <label className="text-sm">Document type (category)
                <select className="w-full border rounded-md h-9 px-2 mt-1" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="edit-category">
                  <option value="">Select…</option>{data.categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </label>
              <label className="text-sm col-span-2">Official display title
                <Input value={editing.display_title} onChange={(e) => setEditing({ ...editing, display_title: e.target.value })} data-testid="edit-title" />
              </label>
              <label className="text-sm col-span-2">Description
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </label>
              <label className="text-sm col-span-2">Official source (agency)
                <Input value={editing.official_source} onChange={(e) => setEditing({ ...editing, official_source: e.target.value })} />
              </label>
              <label className="text-sm col-span-2">Official URL
                <Input value={editing.official_url} onChange={(e) => setEditing({ ...editing, official_url: e.target.value })} data-testid="edit-url" />
              </label>
              <label className="text-sm col-span-2">Official PDF URL (optional)
                <Input value={editing.pdf_url} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} />
              </label>
              <TriSelect label="Witness required" value={editing.witness_required} onChange={(v) => setEditing({ ...editing, witness_required: v })} />
              <TriSelect label="Notarization required" value={editing.notarization_required} onChange={(v) => setEditing({ ...editing, notarization_required: v })} />
              <TriSelect label="Clinician signature" value={editing.clinician_signature_required} onChange={(v) => setEditing({ ...editing, clinician_signature_required: v })} />
              <label className="text-sm">Language
                <Input value={editing.language} onChange={(e) => setEditing({ ...editing, language: e.target.value })} />
              </label>
              <label className="text-sm">Adult or minor
                <select className="w-full border rounded-md h-9 px-2 mt-1" value={editing.adult_or_minor} onChange={(e) => setEditing({ ...editing, adult_or_minor: e.target.value })}>
                  <option>Adult</option><option>Minor</option><option>Both</option>
                </select>
              </label>
              <label className="text-sm">Status
                <select className="w-full border rounded-md h-9 px-2 mt-1" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {data.statuses.map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-sm flex items-center gap-2 mt-6">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published (uncheck to keep as draft/preview)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} className="bg-primary hover:bg-primary/90 text-white" data-testid="save-form-btn">Save</Button>
            </div>
            {reviewDue > 0 && <p className="text-xs text-amber-700 mt-3 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Replacement/outdated forms are never auto-published — review and approve them here.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalFormsAdmin;
